/** Operational table floor UI helpers — display only, no API changes. */

export const OPS_STATUS = {
  available: {
    key: "available",
    label: "Available",
    card: "tf-ops--available",
    badge: "tf-badge--available",
    dot: "bg-emerald-500",
  },
  occupied: {
    key: "occupied",
    label: "Occupied",
    card: "tf-ops--occupied",
    badge: "tf-badge--occupied",
    dot: "bg-blue-500",
  },
  waitingFood: {
    key: "waitingFood",
    label: "In Kitchen",
    card: "tf-ops--kitchen",
    badge: "tf-badge--kitchen",
    dot: "bg-orange-500",
  },
  ready: {
    key: "ready",
    label: "Ready To Serve",
    card: "tf-ops--ready",
    badge: "tf-badge--ready",
    dot: "bg-violet-500",
  },
  billing: {
    key: "billing",
    label: "Billing Requested",
    card: "tf-ops--billing",
    badge: "tf-badge--billing",
    dot: "bg-red-500",
  },
  reserved: {
    key: "reserved",
    label: "Reserved",
    card: "tf-ops--reserved",
    badge: "tf-badge--reserved",
    dot: "bg-violet-400",
  },
  multi: {
    key: "multi",
    label: "Multi Order",
    card: "tf-ops--occupied",
    badge: "tf-badge--occupied",
    dot: "bg-blue-600",
  },
};

export function resolveOpsStatus(table) {
  if (!table) return OPS_STATUS.available;
  if (table.status === "Vacant") return OPS_STATUS.available;
  if (table.status === "Reserved") return OPS_STATUS.reserved;
  if (table.status === "Multi") return OPS_STATUS.multi;

  const phase = table.dining_phase;
  if (phase === "awaiting_payment" || table.status === "Printed") return OPS_STATUS.billing;
  if (phase === "ready" || (table.session?.ready_kot_qty ?? 0) > 0) return OPS_STATUS.ready;

  const inKitchen =
    phase === "kitchen"
    || (table.session?.preparing_kot_qty ?? 0) > 0
    || (table.session?.in_kitchen_qty ?? 0) > 0
    || (phase === "ordering" && (table.session?.pending_kot_qty ?? 0) > 0);

  if (inKitchen) return OPS_STATUS.waitingFood;

  return OPS_STATUS.occupied;
}

export function elapsedAgingTier(minutes) {
  if (minutes == null || minutes < 0) return "fresh";
  if (minutes < 15) return "fresh";
  if (minutes < 30) return "warn";
  return "critical";
}

export function elapsedAgingClass(minutes) {
  const tier = elapsedAgingTier(minutes);
  if (tier === "fresh") return "tf-aging--fresh";
  if (tier === "warn") return "tf-aging--warn";
  return "tf-aging--critical";
}

export function tableGuestCount(table) {
  if (!table) return null;
  if (table.reservation?.party_size) return table.reservation.party_size;
  if (table.capacity) return table.capacity;
  return null;
}

export function tableGuestName(table) {
  if (!table) return null;
  return table.customer || table.reservation?.guest_name || null;
}

export function tableWaiterName(table) {
  if (!table) return null;
  if (table.waiter_name) return table.waiter_name;
  const bills = table.active_bills || table.pending_orders || [];
  const names = [...new Set(bills.map((b) => b.waiter_name).filter(Boolean))];
  if (names.length === 0) return null;
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

export function tableBillAmount(table) {
  if (!table) return 0;
  const amount = table.total_net_amount ?? table.net_amount;
  return Number(amount || 0);
}

export function computeFloorKpis(tables) {
  const vacant = tables.filter((t) => t.status === "Vacant").length;
  const occupied = tables.filter((t) => t.status !== "Vacant").length;
  const openOrders = tables.reduce((sum, t) => sum + (t.active_bill_count || (t.status !== "Vacant" ? 1 : 0)), 0);
  const revenue = tables.reduce((sum, t) => sum + tableBillAmount(t), 0);
  const activeWithBills = tables.filter((t) => tableBillAmount(t) > 0).length;
  const avgTableValue = activeWithBills > 0 ? revenue / activeWithBills : 0;

  return { vacant, occupied, openOrders, revenue, avgTableValue };
}

export function computeAreaCounts(tables, areas) {
  const counts = { All: tables.length };
  areas.forEach((a) => {
    counts[a] = tables.filter((t) => t.area === a).length;
  });
  return counts;
}

/** Status chip counts for the current scope (e.g. selected area). */
export function computeStatusCounts(tables) {
  const counts = { All: tables.length };
  STATUS_FILTER_OPTIONS.forEach(({ key }) => {
    if (key === "All") return;
    counts[key] = tables.filter((t) => statusFilterMatches(t, key)).length;
  });
  return counts;
}

export function buildFloorAlerts(tables) {
  const alerts = [];

  tables.forEach((table) => {
    if (table.status === "Vacant") return;

    const mins = table.elapsed_minutes ?? 0;
    const ops = resolveOpsStatus(table);
    const name = table.name || table.code;

    if (ops.key === "ready") {
      alerts.push({
        id: `ready-${table.id}`,
        type: "ready",
        priority: 1,
        tableId: table.id,
        title: "Ready to serve",
        message: `${name} — food ready`,
      });
    }

    if (ops.key === "billing") {
      alerts.push({
        id: `billing-${table.id}`,
        type: "billing",
        priority: 2,
        tableId: table.id,
        title: "Billing requested",
        message: `${name} — awaiting payment`,
      });
    }

    if (mins >= 20 && ops.key !== "billing") {
      alerts.push({
        id: `delay-${table.id}`,
        type: "delay",
        priority: mins >= 30 ? 0 : 3,
        tableId: table.id,
        title: mins >= 30 ? "Long wait" : "Table aging",
        message: `${name} — ${mins} min`,
      });
    }

    if (ops.key === "waitingFood" && mins >= 15) {
      alerts.push({
        id: `kitchen-${table.id}`,
        type: "kitchen",
        priority: 4,
        tableId: table.id,
        title: "Waiting on kitchen",
        message: `${name} — ${table.dining_phase_label || "In kitchen"}`,
      });
    }
  });

  return alerts.sort((a, b) => a.priority - b.priority).slice(0, 12);
}

export function statusFilterMatches(table, filterKey) {
  if (filterKey === "All") return true;
  const ops = resolveOpsStatus(table);
  return ops.key === filterKey;
}

export const STATUS_FILTER_OPTIONS = [
  { key: "All", label: "All" },
  { key: "available", label: "Available" },
  { key: "occupied", label: "Occupied" },
  { key: "waitingFood", label: "In Kitchen" },
  { key: "ready", label: "Ready" },
  { key: "billing", label: "Billing" },
  { key: "reserved", label: "Reserved" },
];
